// disclosurePanel.ts — the in-app AI DISCLOSURE modal (ⓘ in the Menu). Closes a real store-compliance
// gap: docs/STORE-LISTING.md promises "See our AI disclosure in-app", and Google Play / Apple require
// AI-content disclosure to be reachable in-app. The copy here is condensed verbatim from
// docs/AI-DISCLOSURE.md — it must stay truthful and accurate (not embellished or softened).
//
// Tactical OLED redesign: a full-bleed inset card (scan→correct top rail) with three icon+paragraph
// rows (shield / palette / cpu), each highlighting its load-bearing phrase in the matching accent,
// a divider, the honesty closing line, and a DONE footer. Pure UI over ui.ts + the kit; no Core /
// network / determinism touch.
import Phaser from 'phaser';
import { PALETTE, hex, FONT } from './keys';
import { modal } from './ui';
import { ctaButton, cardPanel, labelText, chip } from './ui/kit';
import { icon } from './icons';
import { openCredits } from './creditsPanel';
import { scaledFont } from './settings';
import { audio } from './audio';
import type { IconName } from './icons';

/** A colored run inside a paragraph. `bold` highlights the load-bearing compliance phrase. */
interface Span {
  text: string;
  color: number;
  bold?: boolean;
}

/** One disclosure row: a left icon + a wrapped, multi-color paragraph. */
interface Row {
  icon: IconName;
  iconColor: number;
  spans: Span[];
}

// The three disclosure rows — copy condensed verbatim from docs/AI-DISCLOSURE.md (do not soften).
// Each row highlights its load-bearing phrase in the row's accent color so the claim reads at a glance.
const ROWS: readonly Row[] = [
  {
    icon: 'shield',
    iconColor: PALETTE.correct,
    spans: [
      { text: 'Trivia boards are written by AI, then ', color: PALETTE.text },
      { text: 'independently fact-checked by a second AI', color: PALETTE.correct, bold: true },
      { text: " before you see them. Answers that don't pass are thrown out.", color: PALETTE.text },
    ],
  },
  {
    icon: 'palette',
    iconColor: PALETTE.scan,
    spans: [
      { text: 'Art, music, and the announcer voice are AI-generated. The announcer is a ', color: PALETTE.text },
      { text: 'synthetic, licensed voice', color: PALETTE.scan, bold: true },
      { text: ' — not a real person.', color: PALETTE.text },
    ],
  },
  {
    icon: 'cpu',
    iconColor: PALETTE.player,
    spans: [
      { text: 'The game itself is ', color: PALETTE.text },
      { text: 'not AI', color: PALETTE.player, bold: true },
      {
        text: ': moves, scoring, and the rival run on a fixed, seeded engine. The rival is classic game-AI — it plays fair, can be wrong like you, and is beatable.',
        color: PALETTE.text,
      },
    ],
  },
];

const CLOSING = 'Nothing here is presented as author-authored.';

/** A word token carrying its color/weight + whether a space precedes it (for spacing + wrap rules). */
interface Tok {
  text: string;
  color: number;
  bold: boolean;
  space: boolean;
}

/**
 * Lay out a multi-color paragraph with manual word-wrap (Phaser Text has no inline color spans).
 * Tokens flow left→right from `textX`, wrapping at `wrapW`; punctuation with no preceding space
 * (e.g. the ":" after "not AI") stays glued to its word. Returns the paragraph's total pixel height.
 */
function flowParagraph(
  scene: Phaser.Scene,
  parent: Phaser.GameObjects.Container,
  textX: number,
  y: number,
  wrapW: number,
  spans: readonly Span[],
  bodyPx: number,
  lineH: number,
): number {
  // Tokenize: split on whitespace but remember whether each word was preceded by a space.
  const toks: Tok[] = [];
  let pendingSpace = false;
  for (const span of spans) {
    for (const part of span.text.split(/(\s+)/)) {
      if (part === '') continue;
      if (/^\s+$/.test(part)) {
        pendingSpace = true;
        continue;
      }
      toks.push({ text: part, color: span.color, bold: !!span.bold, space: pendingSpace });
      pendingSpace = false;
    }
  }

  const spaceW = Math.round(bodyPx * 0.26);
  let lineY = 0;
  let cursorX = 0;
  let firstOnLine = true;
  for (const tok of toks) {
    const t = scene.add
      .text(0, 0, tok.text, {
        fontFamily: FONT.family,
        fontSize: `${bodyPx}px`,
        color: hex(tok.color),
        fontStyle: tok.bold ? 'bold' : 'normal',
      })
      .setOrigin(0, 0);
    const w = t.width;
    // Only a space-preceded token may break to a new line; glued punctuation never orphans.
    if (!firstOnLine && tok.space && cursorX + spaceW + w > wrapW) {
      lineY += lineH;
      cursorX = 0;
      firstOnLine = true;
    }
    const adv = firstOnLine ? 0 : tok.space ? spaceW : 0;
    t.setPosition(textX + cursorX + adv, y + lineY);
    parent.add(t);
    cursorX += adv + w;
    firstOnLine = false;
  }
  return lineY + lineH;
}

/** Open the AI-disclosure modal on a scene. Returns the Modal handle (caller may ignore it). */
export function openDisclosure(scene: Phaser.Scene): ReturnType<typeof modal> {
  const m = modal(scene, { title: 'AI IN TRIVIA MUNCHERS', fullBleed: true, railGrad: [PALETTE.scan, PALETTE.correct] });
  const { card, cardW, bodyTop } = m;

  const panelW = cardW - 52;
  const leftMargin = -panelW / 2 + 20;
  const iconCenterX = leftMargin + 18;
  const textX = leftMargin + 56;
  const wrapW = panelW - 96;
  const bodyPx = scaledFont(cardW > 900 ? 17 : 18, 13);
  const lineH = Math.round(bodyPx * 1.55);

  // Three icon + paragraph rows.
  let y = bodyTop + 18;
  card.add(labelText(scene, -panelW / 2 + 20, y, 'TRANSPARENCY STACK', { size: 12, color: PALETTE.scan }));
  y += 34;
  for (const row of ROWS) {
    const rowH = cardW > 900 ? 88 : 116;
    const rowTop = y - 18;
    card.add(cardPanel(scene, 0, rowTop + rowH / 2, panelW, rowH, { fill: PALETTE.surface, border: PALETTE.lineStrong, accent: row.iconColor }));
    card.add(icon(scene, row.icon, iconCenterX, y + Math.round(bodyPx * 0.62), 30, row.iconColor));
    const h = flowParagraph(scene, card, textX, y, wrapW, row.spans, bodyPx, lineH);
    y += Math.max(rowH - 28, h) + 38;
  }

  // Divider hairline, then the load-bearing honesty closing line (set apart in the muted body color).
  const dividerY = y + 6;
  card.add(scene.add.rectangle(0, dividerY, panelW, 1, PALETTE.line).setOrigin(0.5));
  const closingY = dividerY + 28;
  card.add(
    scene.add
      .text(0, closingY, CLOSING, {
        fontFamily: FONT.family,
        fontSize: `${scaledFont(13, 11)}px`,
        color: hex(PALETTE.text),
        align: 'center',
        wordWrap: { width: cardW - 60 },
      })
      .setOrigin(0.5, 0),
  );

  // CREDITS — a quiet, reachable route to the colophon (preserved; not prominent).
  const creditsY = closingY + 42;
  card.add(
    chip(scene, 0, creditsY, 'Credits', {
      icon: 'caretRight',
      textColor: PALETTE.scan,
      borderColor: PALETTE.scan,
      originX: 0.5,
      height: 44,
      padX: 16,
      fontPx: 12,
      onClick: () => openCredits(scene),
    }),
  );

  // DONE — primary full-width footer CTA, flowed under the content so this legal panel hugs its copy.
  const ctaY = creditsY + 70;
  card.add(ctaButton(scene, 0, ctaY, 'Done', () => m.close(), { width: panelW, height: 56 }));
  m.relayout(ctaY + 42 - bodyTop);

  // Keyboard: ESC closes (mirrors the how-to-play overlay). Capture-phase + stopPropagation so the
  // key drives ONLY this panel and never leaks to the host scene's handlers; torn down on close.
  const onKey = (ev: KeyboardEvent): void => {
    if (ev.key === 'Escape') {
      ev.preventDefault();
      ev.stopPropagation();
      audio().playSfx('uiTap');
      m.close();
    }
  };
  window.addEventListener('keydown', onKey, { capture: true });
  m.layer.once(Phaser.GameObjects.Events.DESTROY, () => {
    window.removeEventListener('keydown', onKey, { capture: true });
  });

  return m;
}
